import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, AlertCircle, CheckCircle2, ArrowLeft, KeyRound } from "lucide-react";
import Layout from "../components/Layout";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

export default function RecuperarPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const inflight = useRef(false);

  // Clean any stale Supabase auth artifacts from previous broken sessions
  useEffect(() => {
    try {
      const keysToCheck = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (k.startsWith("sb-") || (k.includes("supabase") && !k.startsWith("cec-supabase-auth-v2")))) {
          keysToCheck.push(k);
        }
      }
      keysToCheck.forEach((k) => localStorage.removeItem(k));
      // Also clean any pending recovery hash from earlier attempts
      if (window.location.hash && window.location.hash.includes("error")) {
        window.history.replaceState({}, document.title, "/recuperar-password");
      }
    } catch (_) { /* noop */ }
    setError("");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (inflight.current) return;
    setError("");

    if (!isSupabaseConfigured) {
      setError("Autenticação não configurada.");
      return;
    }

    inflight.current = true;
    setBusy(true);
    try {
      const { error: supaError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/redefinir-password`,
      });
      if (supaError) {
        setError(supaError.message);
        return;
      }
      setSent(true);
    } catch (err) {
      setError(err?.message || "Não foi possível enviar o email.");
    } finally {
      setBusy(false);
      inflight.current = false;
    }
  };

  return (
    <Layout>
      <section className="min-h-[70vh] flex items-center py-20">
        <div className="max-w-md mx-auto px-6 w-full">
          <div className="dossier p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-2">
              <KeyRound className="w-4 h-4 text-blood" />
              <p className="mono text-[10px] tracking-[0.3em] text-blood">RECUPERAÇÃO DE ACESSO</p>
            </div>
            <h1 className="headline text-3xl mb-2">Esqueceste a password?</h1>
            <p className="text-bone-muted text-sm mb-8 leading-relaxed">
              Indica o teu email e enviamos-te um link seguro para definires uma nova password.
            </p>

            {sent ? (
              <div className="text-center py-4" data-testid="reset-sent">
                <CheckCircle2 className="w-14 h-14 text-blood mx-auto mb-5" />
                <p className="mono text-[11px] tracking-[0.3em] text-blood mb-3">EMAIL ENVIADO</p>
                <h3 className="headline text-2xl mb-3">Enviámos um link de recuperação para o teu email.</h3>
                <p className="text-bone-muted text-sm leading-relaxed mb-2 break-all">
                  <span className="text-white">{email}</span>
                </p>
                <p className="mono text-[10px] text-bone-faded tracking-[0.2em] leading-relaxed mb-8 mt-4">
                  O link é válido durante 1 hora. Não recebeste? Verifica a pasta de spam.
                </p>
                <Link to="/entrar" data-testid="reset-back-login">
                  <button className="btn-ghost mx-auto">
                    <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao login
                  </button>
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                {!isSupabaseConfigured && (
                  <div className="border border-blood/40 bg-blood/10 p-4 flex gap-3">
                    <AlertCircle className="w-4 h-4 text-blood shrink-0 mt-0.5" />
                    <p className="mono text-[11px] text-bone-dim leading-relaxed">
                      Autenticação ainda não configurada.
                    </p>
                  </div>
                )}
                <div>
                  <label className="mono text-[10px] tracking-[0.3em] text-bone-faded block mb-2">EMAIL</label>
                  <input
                    data-testid="reset-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="cec-input"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                {error && (
                  <p className="mono text-xs text-blood tracking-[0.15em]" data-testid="reset-error">
                    {String(error)}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy || !isSupabaseConfigured}
                  data-testid="reset-submit"
                  className="btn-primary w-full justify-center disabled:opacity-50"
                >
                  {busy ? "A enviar..." : (<>Enviar link de recuperação <Mail className="w-4 h-4" /></>)}
                </button>
              </form>
            )}

            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <Link to="/entrar" className="mono text-[11px] text-bone-muted hover:text-white tracking-[0.2em]" data-testid="reset-cancel">
                <ArrowLeft className="w-3 h-3 inline mr-2" /> Cancelar
              </Link>
            </div>
          </div>
        </div>
      </section>
      <style>{`.cec-input{width:100%;background:#111;border:1px solid rgba(255,255,255,0.1);padding:12px 14px;font-family:'IBM Plex Mono',monospace;font-size:13px;color:#fff;letter-spacing:0.05em}.cec-input:focus{outline:none;border-color:#C8102E}`}</style>
    </Layout>
  );
}
